import { useEffect, useRef, useCallback } from "react";
import { createConsumer, type Subscription } from "@rails/actioncable";

// Singleton consumer instance
let consumerInstance: ReturnType<typeof createConsumer> | null = null;

function getConsumer() {
  if (!consumerInstance) {
    consumerInstance = createConsumer();
  }
  return consumerInstance;
}

export interface ChannelParams {
  channel: string;
  [key: string]: unknown;
}

export interface UseChannelOptions<T> {
  /** Channel params including channel name and any identifiers */
  params: ChannelParams | null;
  /** Callback when data is received */
  onReceived: (data: T) => void;
  /** Optional callback when connected */
  onConnected?: () => void;
  /** Optional callback when disconnected */
  onDisconnected?: () => void;
  /** Whether the subscription is enabled (default: true when params is not null) */
  enabled?: boolean;
}

/**
 * Generic hook for subscribing to ActionCable channels
 *
 * @example
 * // Chat channel
 * useChannel({
 *   params: conversationId ? { channel: "ChatChannel", conversation_id: conversationId } : null,
 *   onReceived: (data) => {
 *     if (data.type === "message_created") {
 *       // handle new message
 *     }
 *   },
 * });
 *
 * @example
 * // Document updates channel
 * useChannel({
 *   params: { channel: "DocumentsChannel" },
 *   onReceived: (data) => {
 *     if (data.type === "document_updated") {
 *       queryClient.invalidateQueries({ queryKey: ["documents"] });
 *     }
 *   },
 * });
 */
export function useChannel<T = unknown>({
  params,
  onReceived,
  onConnected,
  onDisconnected,
  enabled = true,
}: UseChannelOptions<T>) {
  const subscriptionRef = useRef<Subscription | null>(null);
  const onReceivedRef = useRef(onReceived);
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);

  // Keep refs up to date without triggering re-subscriptions
  useEffect(() => {
    onReceivedRef.current = onReceived;
  }, [onReceived]);

  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);

  useEffect(() => {
    onDisconnectedRef.current = onDisconnected;
  }, [onDisconnected]);

  useEffect(() => {
    if (!params || !enabled) {
      return;
    }

    const consumer = getConsumer();

    subscriptionRef.current = consumer.subscriptions.create(params, {
      received: (data: T) => {
        onReceivedRef.current(data);
      },
      connected: () => {
        onConnectedRef.current?.();
      },
      disconnected: () => {
        onDisconnectedRef.current?.();
      },
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [params ? JSON.stringify(params) : null, enabled]);

  const send = useCallback((action: string, data?: Record<string, unknown>) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.perform(action, data);
    }
  }, []);

  return { send };
}

/**
 * Disconnect the global ActionCable consumer
 * Useful for cleanup on logout
 */
export function disconnectConsumer() {
  if (consumerInstance) {
    consumerInstance.disconnect();
    consumerInstance = null;
  }
}
